
import React from 'react';
import { TrackDownload } from '@/lib/types';
import { Card, CardFooter } from '@/components/ui/card';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTrackCard } from './useTrackCard';
import MobileLayout from './MobileLayout';
import DesktopLayout from './DesktopLayout';
import OpenPromoButton from './OpenPromoButton';

interface TrackCardProps {
  track: TrackDownload;
  onCopyUrl: (id: string) => void;
  onDeleteClick: (id: string) => void;
  onSaveEdit: (track: TrackDownload) => Promise<void>;
}

const TrackCard: React.FC<TrackCardProps> = ({ 
  track, 
  onCopyUrl, 
  onDeleteClick,
  onSaveEdit
}) => {
  const isMobile = useIsMobile();
  const {
    isEditing,
    editSpotifyUrl,
    isEditUrlValid,
    editFile,
    isSaving,
    currentFileName,
    isEditOpen,
    editTrackTitle,
    editDescription,
    formatDate,
    handleEditClick,
    handleSpotifyUrlChange,
    handleFileChange,
    handleTitleChange,
    handleDescriptionChange,
    handleCancelEdit,
    handleSaveEdit,
    setIsEditOpen
  } = useTrackCard(track, onSaveEdit);

  // Common edit props for both layouts
  const editProps = {
    spotifyUrl: editSpotifyUrl,
    isUrlValid: isEditUrlValid,
    isSaving,
    currentFileName,
    trackTitle: editTrackTitle,
    description: editDescription,
    onSpotifyUrlChange: handleSpotifyUrlChange,
    onFileChange: handleFileChange,
    onTitleChange: handleTitleChange,
    onDescriptionChange: handleDescriptionChange,
    onSaveEdit: handleSaveEdit,
    onCancelEdit: handleCancelEdit,
    editFile
  };

  // Ensure track ID is always available
  const trackId = track?.id || '';

  return (
    <Card className="transition-all hover:shadow-md border border-border/50 overflow-hidden bg-card/50 backdrop-blur-xs">
      {isMobile ? (
        <MobileLayout
          track={track}
          onCopyUrl={onCopyUrl}
          onDeleteClick={onDeleteClick}
          isEditing={isEditing}
          isEditOpen={isEditOpen}
          setIsEditOpen={setIsEditOpen}
          editProps={editProps}
          formatDate={formatDate}
          handleEditClick={handleEditClick}
        />
      ) : (
        <DesktopLayout
          track={track}
          onCopyUrl={onCopyUrl}
          onDeleteClick={onDeleteClick}
          isEditing={isEditing}
          isEditOpen={isEditOpen}
          setIsEditOpen={setIsEditOpen}
          editProps={editProps}
          formatDate={formatDate}
          handleEditClick={handleEditClick}
        />
      )}
      
      {!isEditing && trackId && (
        <CardFooter className="border-t border-border/30 pt-4">
          <div className="flex justify-between items-center w-full text-sm">
            <OpenPromoButton trackId={trackId} />
          </div>
        </CardFooter>
      )}
    </Card>
  );
};

export default TrackCard;
